import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Alert,
  Table, TableHead, TableBody, TableRow, TableCell,
  Chip, Skeleton, LinearProgress,
} from '@mui/material';
import {
  TrendingUp, TrendingDown, AccountBalance,
} from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import { paperTradingApi, PortfolioValue } from '../services/paperTradingApi';

const PortfolioPage: React.FC = () => {
  const [portfolio, setPortfolio] = useState<PortfolioValue | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    paperTradingApi.getPortfolio()
      .then(setPortfolio)
      .catch((e) => setError(e.message || 'Portfolio konnte nicht geladen werden'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Portfolio - btrader</title>
        </Helmet>
        <Box>
          <Typography variant="h5" fontWeight={700} gutterBottom>Portfolio</Typography>
          <Grid container spacing={3}>
            {[1, 2, 3].map((i) => (
              <Grid item xs={12} md={4} key={i}>
                <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 3 }} />
              </Grid>
            ))}
          </Grid>
          <Skeleton variant="rectangular" height={300} sx={{ mt: 3, borderRadius: 3 }} />
        </Box>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Helmet>
          <title>Portfolio - btrader</title>
        </Helmet>
        <Box>
          <Typography variant="h5" fontWeight={700} gutterBottom>Portfolio</Typography>
          <Alert severity="error">{error}</Alert>
        </Box>
      </>
    );
  }

  if (!portfolio) return null;

  const isProfitable = portfolio.pnl_eur >= 0;

  return (
    <>
      <Helmet>
        <title>Portfolio - btrader</title>
      </Helmet>
      <Box>
        <Typography variant="h5" fontWeight={700} gutterBottom>Portfolio</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Dein Paper-Trading-Portfolio mit aktuellen Marktpreisen.
        </Typography>

        {portfolio.warnings && portfolio.warnings.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {portfolio.warnings.join(' | ')}
          </Alert>
        )}

        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <Card sx={{ borderRadius: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <AccountBalance color="primary" />
                  <Typography variant="body2" color="text.secondary">Gesamtwert</Typography>
                </Box>
                <Typography variant="h4" fontWeight={700}>
                  {'\u20AC'}{portfolio.total_eur.toLocaleString('de-DE', { minimumFractionDigits: 2 })}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Startkapital: {'\u20AC'}{portfolio.initial_eur.toLocaleString('de-DE', { minimumFractionDigits: 2 })}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ borderRadius: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  {isProfitable ? <TrendingUp color="success" /> : <TrendingDown color="error" />}
                  <Typography variant="body2" color="text.secondary">Gewinn / Verlust</Typography>
                </Box>
                <Typography variant="h4" fontWeight={700} color={isProfitable ? 'success.main' : 'error.main'}>
                  {isProfitable ? '+' : ''}{'\u20AC'}{portfolio.pnl_eur.toLocaleString('de-DE', { minimumFractionDigits: 2 })}
                </Typography>
                <Chip
                  label={`${isProfitable ? '+' : ''}${portfolio.pnl_percent.toFixed(2)}%`}
                  size="small"
                  color={isProfitable ? 'success' : 'error'}
                  variant="outlined"
                  sx={{ mt: 0.5 }}
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ borderRadius: 3 }}>
              <CardContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>Performance</Typography>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(Math.max((portfolio.total_eur / portfolio.initial_eur) * 100, 0), 200) / 2}
                  color={isProfitable ? 'success' : 'error'}
                  sx={{ height: 8, borderRadius: 4, mb: 1 }}
                />
                <Typography variant="caption" color="text.secondary">
                  {portfolio.total_eur > portfolio.initial_eur
                    ? `${'\u20AC'}${portfolio.pnl_eur.toFixed(2)} mehr als dein Startkapital`
                    : portfolio.total_eur < portfolio.initial_eur
                      ? `${'\u20AC'}${Math.abs(portfolio.pnl_eur).toFixed(2)} weniger als dein Startkapital`
                      : 'Gleich wie dein Startkapital'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Positions Table */}
        <Card sx={{ borderRadius: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>Positionen</Typography>

            {portfolio.positions.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">
                  Noch keine Positionen. Starte mit einem Paper Trade!
                </Typography>
              </Box>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Währung</TableCell>
                    <TableCell align="right">Menge</TableCell>
                    <TableCell align="right">Preis</TableCell>
                    <TableCell align="right">Wert</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {portfolio.positions.map((pos) => (
                    <TableRow key={pos.currency} hover>
                      <TableCell>
                        <Typography fontWeight={600}>{pos.currency}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        {pos.currency === 'EUR'
                          ? pos.amount.toLocaleString('de-DE', { minimumFractionDigits: 2 })
                          : pos.amount.toFixed(6)}
                      </TableCell>
                      <TableCell align="right">
                        {pos.currency === 'EUR' ? '—' : (
                          pos.price_unavailable
                            ? <Chip label="Kein Preis" size="small" color="warning" variant="outlined" />
                            : `${'\u20AC'}${pos.price.toLocaleString('de-DE', { minimumFractionDigits: 2 })}`
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight={600}>
                          {'\u20AC'}{pos.value_eur.toLocaleString('de-DE', { minimumFractionDigits: 2 })}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </Box>
    </>
  );
};

export default PortfolioPage;
